import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { Navbar } from "@/components/landing/Navbar";
import { CONTACT } from "@/components/landing/data";

export const POLICIES = {
  refund: {
    title: "Refund Policy",
    updated: "Last updated: September 2026",
    intro: "We want every student to feel completely confident when ordering from MyHomeworkAssist. This policy explains, in plain language, when refunds apply and how to request one.",
    sections: [
      { h: "1. Our Commitment", paras: [
        "Every project is handled by a qualified human expert and checked for quality and originality before delivery. If something goes wrong on our side, we will always try to put it right first — and where we cannot, this refund policy applies.",
      ]},
      { h: "2. When You Are Eligible for a Refund", paras: [
        "Non-delivery: if we fail to deliver your work and cannot offer an acceptable solution, you receive a 100% refund.",
        "Late delivery: if your work arrives after the agreed deadline and the delay has made it unusable for you, you may request a full or partial refund depending on the circumstances.",
        "Duplicate or incorrect payment: accidental double payments are refunded in full.",
        "Verified originality failure: if the delivered work fails the agreed originality standard and we cannot correct it, a full refund is issued.",
      ]},
      { h: "3. Partial Refunds", paras: [
        "Where part of the work has been delivered and used, or where an issue affects only a section of the order, we may offer a partial refund of up to 50%, calculated fairly based on the work completed.",
      ]},
      { h: "4. When a Refund Does Not Apply", paras: [
        "Change of mind after an expert has already started your work.",
        "Requests based on instructions or materials that were not shared with us when the order was placed.",
        "Work that has already been submitted, published or otherwise used.",
        "Academic outcomes: our work is provided as reference and learning material, and final grades depend on many factors outside our control, so specific grades cannot be guaranteed.",
      ]},
      { h: "5. Free Revisions First", paras: [
        "Before a refund is considered, we will offer free revisions under our Revision Policy to correct the work against your original brief. Most concerns are resolved at this stage.",
      ]},
      { h: "6. How to Request a Refund", paras: [
        `Email ${CONTACT.email} within 14 days of delivery, quoting your order reference and the reason for your request. Every request is reviewed individually and answered within 5–7 business days. Approved refunds are returned to your original payment method within 7–14 business days.`,
      ]},
      { h: "7. Chargebacks", paras: [
        "Please contact us before raising a dispute with your bank — most issues are resolved faster directly with us. Unwarranted chargebacks may result in the closure of your account.",
      ]},
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: September 2026",
    intro: "These terms govern your use of myhomeworkassist.online and any services you purchase from us. By placing an order, you agree to them.",
    sections: [
      { h: "1. Our Services", paras: [
        "MyHomeworkAssist provides academic support services including model answers, assignment guidance, dissertation and thesis mentoring, research proposal support, proofreading, editing, and presentation materials. All work is produced by human subject specialists.",
      ]},
      { h: "2. Proper Use of Our Work", paras: [
        "Materials we provide are intended as reference, research and learning aids. They are designed to help you understand your subject, structure your own work and improve your academic skills. You are responsible for using them in line with your institution's academic integrity rules.",
      ]},
      { h: "3. Orders, Quotes & Payment", paras: [
        "Quotes are based on the information you provide and remain valid for 7 days. Work begins once payment (or an agreed deposit) is confirmed through our secure third-party checkout. We never see or store your card details.",
      ]},
      { h: "4. Delivery", paras: [
        "Work is delivered to your email address by the agreed deadline. If we anticipate any delay, we will contact you as early as possible with options, including a revised deadline or a refund under our Refund Policy.",
      ]},
      { h: "5. Revisions, Cancellations & Refunds", paras: [
        "These are governed by our Revision Policy, Cancellation Policy and Refund Policy, which form part of these terms.",
      ]},
      { h: "6. Intellectual Property", paras: [
        "Once your order is paid in full, you receive full rights to the delivered materials for your personal academic reference and learning use.",
      ]},
      { h: "7. Confidentiality", paras: [
        "We treat your identity, documents and order details as strictly confidential. Please see our Privacy Policy for full details of how your data is handled.",
      ]},
      { h: "8. Limitation of Liability", paras: [
        "We do not guarantee specific grades or academic outcomes. To the fullest extent permitted by law, our total liability for any claim is limited to the amount you paid for the order in question.",
      ]},
      { h: "9. Governing Law", paras: [
        "These terms are governed by the laws of England and Wales, and any disputes are subject to the exclusive jurisdiction of the courts of England and Wales.",
      ]},
      { h: "10. Contact", paras: [
        `Questions about these terms? Email ${CONTACT.email} or call ${CONTACT.phone}.`,
      ]},
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: September 2026",
    intro: "Your privacy matters to us. This policy explains what information we collect, why, and the control you have over it.",
    sections: [
      { h: "1. Who We Are", paras: [
        `MyHomeworkAssist (myhomeworkassist.online) is an academic support service. For privacy questions, contact ${CONTACT.email}.`,
      ]},
      { h: "2. Information We Collect", paras: [
        "Contact details you give us (name, email, phone/WhatsApp number).",
        "Order information: your university, course, assignment requirements and any brief files you upload.",
        "Payment status information from our payment processor — we never see or store your card details.",
        "Technical data such as IP address, device type and pages visited, collected through analytics cookies.",
      ]},
      { h: "3. How We Use Your Information", paras: [
        "To provide quotes, deliver your orders and communicate with you about your project.",
        "To match you with the right subject expert.",
        "To improve our website and services, and to meet legal or accounting obligations.",
        "We will only send marketing messages with your consent, and you can opt out at any time.",
      ]},
      { h: "4. How We Share It", paras: [
        "We never sell your personal data. We share it only with trusted service providers who help us operate (payment processing, email delivery, hosting and file storage), each bound by confidentiality and data-protection obligations.",
      ]},
      { h: "5. Data Security", paras: [
        "Your data is protected with encryption in transit, access controls and secure cloud storage. Uploaded briefs are accessible only to the team handling your project.",
      ]},
      { h: "6. How Long We Keep It", paras: [
        "Enquiry and order records are kept for up to 24 months for service and accounting purposes. You may ask us to delete your data earlier — see Your Rights below.",
      ]},
      { h: "7. Your Rights", paras: [
        "Under UK GDPR you can request access to your data, correction, deletion, restriction or portability, and you can object to processing. To exercise any right, email us — we respond within 30 days.",
      ]},
      { h: "8. Cookies", paras: [
        "We use essential cookies to run the site and analytics cookies to understand how it is used. You can control or delete cookies through your browser settings.",
      ]},
      { h: "9. Changes to This Policy", paras: [
        `We may update this policy from time to time; the latest version is always on this page. Questions? Email ${CONTACT.email}.`,
      ]},
    ],
  },
  revision: {
    title: "Revision Policy",
    updated: "Last updated: September 2026",
    intro: "Your work should match your brief exactly. If it doesn't, we fix it — free.",
    sections: [
      { h: "1. Our Free Revision Promise", paras: [
        "Every order includes free revisions for 14 days from delivery (30 days for dissertations and theses). There is no limit on the number of reasonable revision rounds within that window.",
      ]},
      { h: "2. What Counts as a Revision", paras: [
        "Adjustments that bring the work in line with your original instructions: structure, clarity, argument flow, referencing style, formatting, or points from your brief that were missed.",
      ]},
      { h: "3. What Counts as New Work", paras: [
        "Requests that change the original brief — a new topic, additional word count, different requirements or new source materials — are quoted separately at a fair, reduced rate rather than treated as revisions.",
      ]},
      { h: "4. How to Request a Revision", paras: [
        `Reply to your delivery email or message us on WhatsApp with your order reference and clear comments on what to change. The more specific your notes, the faster the turnaround.`,
      ]},
      { h: "5. Revision Turnaround Times", paras: [
        "Minor edits: within 24 hours. Moderate changes: within 48 hours. Substantial rework: within 72 hours. Urgent deadlines? Tell us and we will prioritise.",
      ]},
      { h: "6. If We Can't Make It Right", paras: [
        "If repeated revisions still don't meet the agreed brief, our Refund Policy applies — your payment is protected.",
      ]},
    ],
  },
  cancellation: {
    title: "Cancellation Policy",
    updated: "Last updated: September 2026",
    intro: "Plans change. This policy explains how cancellations work at each stage of your order.",
    sections: [
      { h: "1. Before Work Begins", paras: [
        "If you cancel before an expert has been assigned to your project, you receive a full refund of the amount paid.",
      ]},
      { h: "2. After an Expert Has Been Assigned", paras: [
        "If work has started, a partial refund is offered based on the progress completed — typically 50–70% when cancelled early, reducing as delivery approaches. We will always tell you the exact figure before you decide.",
      ]},
      { h: "3. After Delivery", paras: [
        "Once your work has been delivered, the order can no longer be cancelled. If you have concerns about the delivered work, our Revision Policy and Refund Policy apply instead.",
      ]},
      { h: "4. How to Cancel", paras: [
        `Email ${CONTACT.email} or message us on WhatsApp with your order reference as soon as possible — the earlier you cancel, the higher the refund.`,
      ]},
      { h: "5. Refund Processing", paras: [
        "Approved cancellation refunds are processed to your original payment method within 7–14 business days.",
      ]},
      { h: "6. If We Cancel", paras: [
        "In rare cases we may need to decline or cancel an order — for example, if the brief is incomplete or falls outside our expertise. In such cases you always receive a 100% refund of any payment made.",
      ]},
    ],
  },
};

export const PolicyPage = ({ policyKey }) => {
  const policy = POLICIES[policyKey];
  if (!policy) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F2]" data-testid={`policy-page-${policyKey}`}>
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-16 pt-[132px] sm:px-8 sm:pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#A07E1F]">
            <ShieldCheck size={15} /> {policy.updated}
          </p>
          <h1 className="mt-5 font-serif-display text-4xl font-medium tracking-tight text-[#12294D] sm:text-5xl" data-testid="policy-title">
            {policy.title}
          </h1>
          <p className="mt-5 border-l-2 border-[#D4AF37]/60 pl-5 font-serif-display text-lg italic text-[#46586F]">{policy.intro}</p>
          <div className="mt-12 space-y-10" data-testid="policy-content">
            {policy.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-serif-display text-2xl font-semibold text-[#12294D]">{s.h}</h2>
                {s.paras.map((p, j) => (
                  <p key={j} className="mt-3 text-[15px] leading-relaxed text-[#46586F]">{p}</p>
                ))}
              </section>
            ))}
          </div>
          <div className="luxury-card mt-14 rounded-xl p-7" data-testid="policy-contact-card">
            <p className="text-sm text-[#46586F]">
              Questions about this policy? Email <a href={`mailto:${CONTACT.email}`} className="font-semibold text-[#A07E1F] underline underline-offset-4">{CONTACT.email}</a> or
              call <a href="tel:+442073626786" className="font-semibold text-[#A07E1F] underline underline-offset-4">{CONTACT.phone}</a> — we reply within minutes, 24/7.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
